// Données de la carte du territoire : limites statiques (/donnees-geo, OpenStreetMap sous ODbL et
// tracés indicatifs de la maquette), territoire de la collectivité (GET /territoire), dossiers
// localisés (GET /dossiers?localise=oui). Construit les échelles Gabon, province, commune (ou
// département pour un conseil départemental) et arrondissements, sans jamais inventer de limite :
// une échelle sans source reste sans géométrie et la carte l'écrit.
import type { Dossier } from '@cc/contrats';
import type { ListePaginee } from '@cc/contrats/ui-metier';
import type { GeometriePolygonale, MarqueurCarte, Territoire } from '@cc/ui';
import { useQueries, useQuery } from '@tanstack/react-query';
import { requete } from '../../api/client';

export interface ProprietesLimite {
  code: string;
  code_officiel: string | null;
  nom: string;
  commune: string | null;
  province: string | null;
  niveau: string;
  numero?: number;
  indicatif: boolean;
  source: string;
  date_source: string | null;
  licence: string;
}
export interface Limite {
  properties: ProprietesLimite;
  geometry: GeometriePolygonale;
}
interface Collection {
  features: Limite[];
}

export interface TerritoireApi {
  collectivite: {
    id: string;
    nom: string;
    code_court: string;
    type: string;
    fictive: boolean;
    province: { code: string; nom: string };
    departement: { code: string; nom: string } | null;
    geometrie: GeometriePolygonale | null;
  };
  arrondissements: { id: string; nom: string; numero: number | null; geometrie: GeometriePolygonale | null }[];
  arrondissement_utilisateur: string | null;
  dossiers_localises: number;
}

export interface Presentation {
  collectivites: Record<string, { commune?: string; departement?: string; mention: string }>;
}

export interface Manifeste {
  genere_le: string;
  extraction_osm: string | null;
  attribution: string;
  fichiers: { chemin: string; niveau: string; entites: number; octets: number; source: string; licence: string; indicatif: boolean }[];
  relations_ignorees: string[];
}

export const FICHIERS = {
  pays: 'gabon/pays.geojson',
  provinces: 'gabon/provinces.geojson',
  departements: 'gabon/departements.geojson',
  communes: 'communes/communes.geojson',
  arrondissements: 'arrondissements/arrondissements.geojson',
  indicatifs: 'arrondissements/libreville-indicatifs.geojson',
} as const;
export type NomFichier = keyof typeof FICHIERS;
const SOURCE_OSM = 'OpenStreetMap (ODbL)';
const SOURCE_INDICATIVE = 'tracé indicatif de maquette, sans valeur administrative';

async function lireJson<T>(chemin: string, signal?: AbortSignal): Promise<T> {
  const r = await fetch(`/donnees-geo/${chemin}`, { signal });
  if (!r.ok) throw new Error(`Données géographiques indisponibles : ${chemin} (${r.status})`);
  return (await r.json()) as T;
}

const optionsStatiques = { staleTime: Infinity, gcTime: 60 * 60_000, retry: 1 } as const;

export function useTerritoire() {
  return useQuery({ queryKey: ['territoire'], queryFn: ({ signal }) => requete<TerritoireApi>('/territoire', { signal }), staleTime: 5 * 60_000 });
}

export function useManifeste() {
  return useQuery({ queryKey: ['donnees-geo', 'index'], queryFn: ({ signal }) => lireJson<Manifeste>('index.json', signal), ...optionsStatiques });
}

/**
 * Charge les fichiers de limites demandés ; seuls les fichiers « requis » conditionnent l'état de
 * chargement, les autres (niveaux nationaux demandés après coup) complètent la carte sans la démonter.
 */
export function useLimites(noms: readonly NomFichier[], requis: readonly NomFichier[] = noms) {
  const presentation = useQuery({ queryKey: ['donnees-geo', 'presentation'], queryFn: ({ signal }) => lireJson<Presentation>('presentation.json', signal), ...optionsStatiques });
  const fichiers = useQueries({
    queries: noms.map((nom) => ({ queryKey: ['donnees-geo', nom], queryFn: ({ signal }: { signal?: AbortSignal }) => lireJson<Collection>(FICHIERS[nom], signal), ...optionsStatiques })),
  });
  const limites: Partial<Record<NomFichier, Limite[]>> = {};
  noms.forEach((nom, i) => {
    const d = fichiers[i]?.data;
    if (d) limites[nom] = d.features;
  });
  return {
    limites,
    presentation: presentation.data ?? null,
    chargement: fichiers.some((f, i) => requis.includes(noms[i] as NomFichier) && f.isPending) || presentation.isPending,
    erreur: fichiers.find((f) => f.error)?.error ?? null,
  };
}

/** Dossiers localisés visibles par l'utilisateur (au plus 200), pour les marqueurs. */
export function useDossiersLocalises(actif = true, limite = 200) {
  return useQuery({
    queryKey: ['dossiers', 'localises', limite],
    queryFn: ({ signal }) => requete<ListePaginee<Dossier>>('/dossiers', { signal, parametres: { localise: 'oui', limite: String(limite) } }).then((r) => r.elements),
    enabled: actif,
    retry: false,
  });
}

export const ETATS_TERMINES = new Set(['cloture', 'archive', 'classe_sans_suite', 'retire']);

export function tonDossier(d: Dossier): MarqueurCarte['ton'] {
  if (d.en_retard) return 'retard';
  return ETATS_TERMINES.has(d.etat) ? 'autre' : 'ouvert';
}

/** Marqueurs numérotés dans l'ordre de la liste (même numéro des deux côtés). */
export function marqueursDossiers(dossiers: Dossier[]): MarqueurCarte[] {
  return dossiers
    .filter((d) => d.localisation)
    .map((d, i) => ({
      id: d.id,
      numero: i + 1,
      longitude: (d.localisation as NonNullable<Dossier['localisation']>).longitude,
      latitude: (d.localisation as NonNullable<Dossier['localisation']>).latitude,
      libelle: `${d.numero ?? 'Sans numéro'} · ${d.demarche.libelle}${d.demandeur.nom ? ` · ${d.demandeur.nom}` : ''}`,
      ton: tonDossier(d),
    }));
}

export function normaliser(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/^(commune|conseil departemental|departement)\s+(de la |de l'|des |du |de |d')/, '')
    .replace(/[^a-z0-9]/g, '');
}

const voisin = (l: Limite) => ({ id: l.properties.code, libelle: l.properties.nom, geometrie: l.geometry, indicatif: l.properties.indicatif });
const source = (l: Limite | undefined) => (l ? (l.properties.indicatif ? SOURCE_INDICATIVE : SOURCE_OSM) : null);

export interface Echelles {
  echelles: Territoire[];
  /** Identifiant de l'échelle à afficher d'abord (arrondissement de l'agent, sinon la commune, sinon la province). */
  defaut: string;
  /** Avertissement à afficher (collectivité de présentation, limite absente). */
  mention: string | null;
  /** Nom de la commune réelle dont les limites sont affichées, s'il y en a une. */
  communeAffichee: string | null;
}

/** Territoire de l'échelle « commune » : géométrie de la base, rapprochement par le nom, sinon correspondance de présentation. */
function echelleCommune(t: TerritoireApi, limites: Partial<Record<NomFichier, Limite[]>>, presentation: Presentation | null): { territoire: Territoire; mention: string | null; commune: string | null } {
  const c = t.collectivite;
  const communes = limites.communes ?? [];
  const departements = limites.departements ?? [];
  const departementsProvince = departements.filter((d) => d.properties.province === c.province.nom);
  const base = { id: 'commune', niveau: (c.type === 'conseil_departemental' ? 'departement' : 'commune') as Territoire['niveau'], libelle: c.nom, indicatif: false };
  const nom = normaliser(c.nom);
  if (c.geometrie) return { territoire: { ...base, geometrie: c.geometrie, source: 'limite enregistrée par la collectivité', voisins: departementsProvince.map(voisin) }, mention: null, commune: c.nom };
  let limite = c.type === 'conseil_departemental' ? departements.find((d) => normaliser(d.properties.nom) === nom) : communes.find((l) => normaliser(l.properties.nom) === nom);
  let mention: string | null = null;
  const correspondance = c.fictive ? presentation?.collectivites[c.code_court] : undefined;
  if (!limite && correspondance) {
    limite = correspondance.commune ? communes.find((l) => l.properties.code === correspondance.commune) : departements.find((d) => d.properties.code === correspondance.departement);
    mention = limite ? correspondance.mention : null;
  }
  if (!limite) return { territoire: { ...base, geometrie: null, source: null, voisins: departementsProvince.map(voisin) }, mention: `Aucune limite officielle ni libre n'est disponible pour ${c.nom} : l'échelle de la commune n'est pas dessinée.`, commune: null };
  const autres = departementsProvince.filter((d) => d.properties.code !== limite?.properties.code && normaliser(d.properties.nom) !== normaliser(limite?.properties.nom ?? ''));
  return { territoire: { ...base, libelle: mention ? `${c.nom} (exemple : ${limite.properties.nom})` : c.nom, geometrie: limite.geometry, source: source(limite), voisins: autres.map(voisin) }, mention, commune: limite.properties.nom };
}

/** Fichiers nécessaires d'emblée (les niveaux national et provincial se chargent à la demande). */
export function fichiersInitiaux(t: TerritoireApi | undefined): NomFichier[] {
  return t?.collectivite.type === 'conseil_departemental' ? ['communes', 'departements', 'arrondissements', 'indicatifs'] : ['communes', 'arrondissements', 'indicatifs'];
}

/** Fichier à charger quand une échelle différée est choisie. */
export function fichierPourEchelle(id: string): NomFichier | null {
  return id === 'gabon' ? 'pays' : id === 'province' ? 'provinces' : null;
}

export function construireEchelles(t: TerritoireApi, limites: Partial<Record<NomFichier, Limite[]>>, presentation: Presentation | null): Echelles {
  const echelles: Territoire[] = [];
  const pays = (limites.pays ?? [])[0];
  echelles.push({ id: 'gabon', niveau: 'pays', libelle: 'Gabon', geometrie: pays?.geometry ?? null, indicatif: false, source: pays ? SOURCE_OSM : null, differe: !limites.pays, voisins: [] });
  const provinces = limites.provinces ?? [];
  const province = provinces.find((p) => p.properties.code === t.collectivite.province.code);
  echelles.push({
    id: 'province',
    niveau: 'province',
    libelle: t.collectivite.province.nom,
    geometrie: province?.geometry ?? null,
    indicatif: false,
    source: source(province),
    differe: !limites.provinces,
    voisins: provinces.filter((p) => p !== province).map(voisin),
  });
  const commune = echelleCommune(t, limites, presentation);
  echelles.push(commune.territoire);

  const reels = (limites.arrondissements ?? []).filter((a) => commune.commune && normaliser(a.properties.commune ?? '') === normaliser(commune.commune));
  const indicatifs = (limites.indicatifs ?? []).filter((a) => commune.commune && normaliser(a.properties.commune ?? '') === normaliser(commune.commune));
  const resolus = t.arrondissements.map((a) => {
    const limite = a.numero !== null ? (reels.find((r) => r.properties.numero === a.numero) ?? indicatifs.find((r) => r.properties.numero === a.numero)) : undefined;
    return { a, geometrie: a.geometrie ?? limite?.geometry ?? null, indicatif: a.geometrie ? false : Boolean(limite?.properties.indicatif), source: a.geometrie ? 'limite enregistrée par la collectivité' : source(limite) };
  });
  for (const r of resolus) {
    echelles.push({
      id: `arrondissement:${r.a.id}`,
      niveau: 'arrondissement',
      libelle: r.a.nom,
      geometrie: r.geometrie,
      indicatif: r.indicatif,
      source: r.source,
      voisins: resolus.filter((x) => x !== r && x.geometrie).map((x) => ({ id: x.a.id, libelle: x.a.nom, geometrie: x.geometrie as GeometriePolygonale, indicatif: x.indicatif })),
    });
  }
  const utilisateur = resolus.find((r) => r.a.id === t.arrondissement_utilisateur && r.geometrie);
  const defaut = utilisateur ? `arrondissement:${utilisateur.a.id}` : commune.territoire.geometrie ? 'commune' : 'province';
  return { echelles, defaut, mention: commune.mention, communeAffichee: commune.commune };
}
